import NextLink from 'next/link'
import { ButtonTypes, IconPositions } from '../../types/enums'
import {
  IconButton,
  IconButtonProps,
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  chakra,
} from '@chakra-ui/react'
import ComponentProps from '../../types/ComponentProps'
import {
  ContactInteractionEvent,
  FileDownloadEvent,
  FormSubmitEvent,
  ContactInteractionProps,
  FileDownloadProps,
  FormSubmitProps,
  NavTrackingProps,
  SocialInteractionProps,
  NavigationTrackingEvent,
  SocialInteractionEvent,
} from './ButtonTracking'

import {
  ContentInteractionEvent,
  ContentInteractionProps,
} from '../../utils/GeneralTracking'

export interface ButtonProps extends ComponentProps {
  text?: string
  url?: string
  target?: string
  icon?: JSX.Element
  iconProps?: IconButtonProps
  iconPosition?: IconPositions
  buttonType?: ButtonTypes
  buttonProps?: ChakraButtonProps
  isDownloadLink?: boolean
  ariaLabel?: string
  navigationTrackingEventProps?: NavTrackingProps
  contentInteractionEventProps?: ContentInteractionProps
  contactInteractionEventProps?: ContactInteractionProps
  socialInteractionEventProps?: SocialInteractionProps
  fileDownloadEventProps?: FileDownloadProps
  formSubmitEventProps?: FormSubmitProps
  onClick?: () => void
}

/**
 * A versatile component that utilizes Chakra UI's `<Button>` and `<ButtonIcon>` components.
 *
 * Depending on the presence of a `url` prop, this component conditionally renders either an
 * anchor (`<a>`) tag or a button (`<button>`) tag.
 *
 * It integrates seamlessly with Next.js's `<Link>` component using
 * [legacyBehavior](https://nextjs.org/docs/pages/api-reference/components/link#legacybehavior) and `passHref`,
 * allowing the `Link` component to pass the `href` prop to its child and dynamically render the appropriate element.
 *
 * @param {string} [text] - The text displayed within the Button component.
 * @param {string} [url] - The URL to navigate to when the component is clicked. If provided, renders an anchor tag.
 * @param {string} [target] - The target attribute specifies where to open the link
 * @param {JSX.Element} [icon] - An icon element displayed inside the button.
 * @param {IconProps} [iconProps] - Props for styling the icon within the button.
 * @param {IconPositions} [iconPosition] - Determines the position of the icon relative to the text, either 'Left' or 'Right'.
 * @param {ButtonTypes} [buttonType] - Specifies the button type, such as `Default`, `IconOnly`, or `TextOnly`.
 * @param {ButtonProps} [buttonProps] - Additional props for customizing the button component.
 * @param {string} [ariaLabel] - An accessible label for the button, used by screen readers. Required if the button contains only an icon without accompanying text.
 * @param {function} [onClick] - The function to call when the button is clicked.
 * @param {NavTrackingProps} [navigationTrackingEventProps] - Props for tracking navigation events.
 * @param {ContentInteractionProps} [contentInteractionEventProps] - Props for tracking content interaction events.
 * @param {ContactInteractionProps} [contactInteractionEventProps] - Props for tracking contact-related interactions.
 * @param {SocialInteractionProps} [socialInteractionEventProps] - Props for tracking social interaction events.
 * @param {FileDownloadProps} [fileDownloadEventProps] - Props for tracking file download events.
 * @param {FormSubmitProps} [formSubmitEventProps] - Props for tracking form submission events.
 *
 * @returns {JSX.Element} A rendered Button or Link component, based on the provided props.
 */
export default chakra(Button)
function Button({
  text,
  url,
  target,
  icon,
  iconProps,
  iconPosition = IconPositions.Right,
  buttonType = ButtonTypes.Default,
  isDownloadLink,
  buttonProps,
  ariaLabel,
  onClick,
  navigationTrackingEventProps,
  contentInteractionEventProps,
  contactInteractionEventProps,
  socialInteractionEventProps,
  fileDownloadEventProps,
  formSubmitEventProps,
  children,
  ...props
}: ButtonProps) {
  const elementType = url ? NextLink : 'button'
  const showIcon = buttonType !== ButtonTypes.TextOnly && icon

  function ButtonClickEvent() {
    onClick && onClick()

    // Handle tracking events
    navigationTrackingEventProps &&
      NavigationTrackingEvent(navigationTrackingEventProps)
    contentInteractionEventProps &&
      ContentInteractionEvent(contentInteractionEventProps)
    contactInteractionEventProps &&
      ContactInteractionEvent(contactInteractionEventProps)
    socialInteractionEventProps &&
      SocialInteractionEvent(socialInteractionEventProps)
    fileDownloadEventProps && FileDownloadEvent(fileDownloadEventProps)
    formSubmitEventProps && FormSubmitEvent(formSubmitEventProps)
  }

  return buttonType === ButtonTypes.IconOnly && icon ? (
    <IconButton
      as={elementType}
      href={url ?? null}
      target={target}
      icon={icon}
      download={isDownloadLink}
      aria-label={`${ariaLabel}`}
      onClick={ButtonClickEvent}
      {...iconProps}
      {...props}
    />
  ) : (
    <ChakraButton
      as={elementType}
      href={url ?? null}
      target={target}
      cursor='pointer'
      download={isDownloadLink}
      onClick={ButtonClickEvent}
      aria-label={`${ariaLabel}`}
      leftIcon={
        showIcon && iconPosition === IconPositions.Left ? icon : undefined
      }
      rightIcon={
        showIcon && iconPosition === IconPositions.Right ? icon : undefined
      }
      {...buttonProps}
      {...props}
    >
      {children ? children : text}
    </ChakraButton>
  )
}
